
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronRight, ChevronDown, Zap, ShieldCheck, BarChart3, Clock, Target } from 'lucide-react';
import Image1 from "../../assets/ecommerce.webp";
import Image2 from "../../assets/ecommerce2.png";

const Ecommerce = () => {
  const [openIndex, setOpenIndex] = useState(0);
  const navigate = useNavigate();

  const handleSignupClick = () => {
    navigate("/login?mode=signup");
  };

  const ecommerceSolutions = [
    {
      title: "Real-Time Order Tracking & Updates",
      description: "Keep customers informed from checkout to doorstep. Automatically send SMS alerts when orders are confirmed, shipped, or out for delivery, reducing 'Where is my order?' inquiries."
    },
    {
      title: "Secure Two-Factor Authentication (2FA)",
      description: "Protect user accounts and transactions with instant OTP (One-Time Password) delivery. Our high-priority routes ensure codes arrive in seconds, minimizing login friction."
    },
    {
      title: "Abandoned Cart Recovery",
      description: "Recover lost sales by sending automated, personalized SMS reminders to customers who left items in their cart. Include direct links or discount codes to drive immediate conversions."
    },
    {
      title: "Personalized Promotional Campaigns",
      description: "Drive seasonal sales and flash deals with targeted bulk SMS. Segment your audience based on purchase history to send relevant offers that boast a 98% open rate."
    },
    {
      title: "Automated Feedback & Reviews",
      description: "Build social proof by automatically requesting product reviews via SMS shortly after delivery. Higher response rates through mobile messaging help boost your store's credibility."
    }
  ];

  // E-commerce-specific workflow tags for the 4th section
  const row1 = ["Order Confirmed", "OTP Verified", "Payment Success", "Flash Sale Alert", "Package Shipped", "Track My Order", "In-Stock Notification"];
  const row2 = ["Cart Abandoned", "Exclusive Discount", "Review Our Product", "Delivery Today", "Refund Processed", "Loyalty Points", "Welcome Offer"];

  return (
    <div className="w-full font-sans text-slate-900 overflow-x-hidden">
      {/* Premium Animation Styles */}
      <style>
        {`
          @keyframes scroll-left {
            0% { transform: translateX(0); }
            100% { transform: translateX(-50%); }
          }
          @keyframes scroll-right {
            0% { transform: translateX(-50%); }
            100% { transform: translateX(0); }
          }
          .animate-scroll-left {
            display: flex;
            width: max-content;
            animation: scroll-left 40s linear infinite;
          }
          .animate-scroll-right {
            display: flex;
            width: max-content;
            animation: scroll-right 40s linear infinite;
          }
          .pause-on-hover:hover {
            animation-play-state: paused;
          }
          @keyframes bounce-right {
            0%, 100% { transform: translateX(0); }
            50% { transform: translateX(3px); }
          }
          .animate-bounce-right {
            animation: bounce-right 1s infinite;
          }
        `}
      </style>

 {/* --- SECTION 1: HERO SECTION --- */}
      <section className="bg-[#EEF2FC] flex flex-col md:flex-row items-center justify-between px-6 py-10 md:px-12 lg:px-24 min-h-[600px]">
        <div className="w-full md:w-1/2 mb-10 md:mb-0 md:pr-10 pt-12 md:pt-1 flex flex-col items-start -mt-16">
          <h1 className="text-4xl md:text-5xl font-medium text-slate-900 leading-tight tracking-tight mb-6">
            From Cart to Confirmation — Instant SMS for Every Transaction
          </h1>
          <p className="text-lg text-slate-700 mb-8 leading-relaxed">
            Eurofox Bulk SMS solution empowers e-commerce businesses with instant order updates, 
            OTP authentication, delivery alerts, and promotional campaigns — ensuring higher 
            engagement and better customer experience.
          </p>
          <button
            onClick={() => navigate('/contact')}
            className="inline-flex items-center gap-2 px-6 py-3 text-white bg-slate-900 rounded-lg transition duration-300 shadow-md"
          >
            Request a Demo
            <ChevronRight className="w-5 h-5 animate-bounce-right" />
          </button>
        </div>

        <div className="w-full md:w-1/2 flex justify-center relative">
          <img
            src={Image1}
            alt="Ecommerce Hero"
            className="relative object-contain rounded-xl 
                       w-full max-w-[280px] sm:max-w-sm h-auto mx-auto 
                       md:w-auto md:max-w-lg md:-left-[10px] md:top-[14px] md:h-[400px] md:transform md:scale-125 md:origin-center"
          />
        </div>
      </section>

      
      {/* --- SECTION 2: E-COMMERCE PROCESSES (Accordion Section) --- */}
      <section className="bg-[#EEF2FC] py-14 md:py-20 lg:py-32 -mt-10 md:-mt-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-12">
          {/* HEADING */}
          <div className="text-center max-w-4xl mx-auto mb-10 md:mb-16 lg:mb-24">
            <h2 className="text-3xl sm:text-4xl md:text-4xl lg:text-6xl font-medium text-slate-900 leading-tight">
              How Eurofox Streamlines <br />
              <span className="text-[#F46653]">E-Commerce Processes</span>
            </h2>
          </div>

          {/* CONTENT */}
          <div className="flex flex-col lg:flex-row items-center gap-8 md:gap-12 lg:gap-16">
            {/* IMAGE (Left Side) */}
            <div className="lg:w-5/12 w-full">
              <div className="relative rounded-2xl overflow-hidden shadow-lg w-full h-[240px] sm:h-[300px] lg:h-[530px]">
                <img
                  src={Image2}
                  alt="Eurofox E-Commerce Process"
                  className="w-full h-full object-cover object-center"
                />
              </div>
            </div>

            {/* ACCORDION (Right Side) */}
            <div className="lg:w-7/12 w-full space-y-3 sm:space-y-4">
              {ecommerceSolutions.map((item, index) => (
                <div
                  key={index}
                  className="bg-white border border-slate-200 rounded-xl lg:rounded-2xl shadow-sm transition-all duration-300"
                >
                  <button
                    onClick={() => setOpenIndex(openIndex === index ? -1 : index)}
                    className="w-full flex items-center justify-between p-4 sm:p-5 lg:p-6 text-left hover:bg-slate-50 transition-colors rounded-xl lg:rounded-2xl"
                  >
                    <span className="font-medium text-slate-800 text-base sm:text-lg lg:text-xl pr-4">
                      {item.title}
                    </span>
                    <ChevronDown
                      className={`w-5 h-5 text-slate-400 transition-transform duration-300 flex-shrink-0 ${
                        openIndex === index ? "rotate-180" : ""
                      }`}
                    />
                  </button>

                  <div
                    className={`overflow-hidden transition-all duration-300 ease-in-out ${
                      openIndex === index ? "max-h-60 opacity-100" : "max-h-0 opacity-0"
                    }`}
                  >
                    <div className="px-4 sm:px-6 pb-5 text-slate-600 text-sm sm:text-base lg:text-lg leading-relaxed border-t border-slate-50 pt-4">
                      {item.description}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* --- SECTION 3: WHY CHOOSE EUROFOX FOR E-COMMERCE --- */}
      <section className="bg-[#EEF2FC] py-20 lg:py-32">
        <div className="container mx-auto px-6 lg:px-24">
          
          {/* HEADING */}
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl md:text-5xl font-medium text-slate-900 mb-6 leading-tight -mt-16">
              Powerful Messaging Built for <br />
              <span className="text-[#F46653]">Retail Scale & Growth</span>
            </h2>
            <p className="text-slate-700 text-lg">
              Deliver seamless shopping experiences with a communication infrastructure 
              designed specifically for modern e-commerce requirements.
            </p>
          </div>

          {/* BENEFITS GRID */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            
            {/* CARD 1 */}
            <div className="group p-8 bg-[#D0DCF2] rounded-2xl border border-transparent  hover:bg-white hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2">
              <div className="w-14 h-14 bg-white rounded-xl flex items-center justify-center mb-6 shadow-sm group-hover:bg-[#F46653] transition-colors duration-500">
                <Zap className="w-8 h-8 text-[#F46653] group-hover:text-white transition-colors duration-500" />
              </div>
              <h3 className="text-xl font-semibold text-slate-900 mb-3">Instant OTPs</h3>
              <p className="text-slate-700 leading-relaxed text-sm lg:text-base">
                Ensure account security with sub-5 second delivery for login and payment verifications.
              </p>
            </div>

            {/* CARD 2 */}
            <div className="group p-8 bg-[#D0DCF2] rounded-2xl border border-transparent hover:bg-white hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2">
              <div className="w-14 h-14 bg-white rounded-xl flex items-center justify-center mb-6 shadow-sm group-hover:bg-[#F46653] transition-colors duration-500">
                <ShieldCheck className="w-8 h-8 text-[#F46653] group-hover:text-white transition-colors duration-500" />
              </div>
              <h3 className="text-xl font-semibold text-slate-900 mb-3">DND Management</h3>
              <p className="text-slate-700 leading-relaxed text-sm lg:text-base">
                Smart routing that complies with local regulations while ensuring your messages reach your customers.
              </p>
            </div>

            {/* CARD 3 */}
            <div className="group p-8 bg-[#D0DCF2] rounded-2xl border border-transparent hover:bg-white hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2">
              <div className="w-14 h-14 bg-white rounded-xl flex items-center justify-center mb-6 shadow-sm group-hover:bg-[#F46653] transition-colors duration-500">
                <BarChart3 className="w-8 h-8 text-[#F46653] group-hover:text-white transition-colors duration-500" />
              </div>
              <h3 className="text-xl font-semibold text-slate-900 mb-3">Advanced Analytics</h3>
              <p className="text-slate-700 leading-relaxed text-sm lg:text-base">
                Track delivery rates and click-through metrics to optimize your promotional campaigns.
              </p>
            </div>

            {/* CARD 4 */}
            <div className="group p-8 bg-[#D0DCF2] rounded-2xl border border-transparent hover:bg-white hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2">
              <div className="w-14 h-14 bg-white rounded-xl flex items-center justify-center mb-6 shadow-sm group-hover:bg-[#F46653] transition-colors duration-500">
                <Clock className="w-8 h-8 text-[#F46653] group-hover:text-white transition-colors duration-500" />
              </div>
              <h3 className="text-xl font-semibold text-slate-900 mb-3">Global Reach</h3>
              <p className="text-slate-700 leading-relaxed text-sm lg:text-base">
                Expand your store internationally with high-priority direct routes to over 190+ countries.
              </p>
            </div>
          </div>

          {/* PROFESSIONAL CTA BANNER */}
          <div className="mt-20 p-8 md:p-16 bg-slate-900 rounded-[2.5rem] text-center overflow-hidden relative shadow-2xl">
             <div className="relative z-10 max-w-2xl mx-auto">
                <h3 className="text-2xl md:text-4xl text-white font-medium mb-8">Ready to transform your customer communication?</h3>
                <button 
                   onClick={() => navigate('/contact')}
                   className="px-10 py-4 bg-[#F46653] text-white rounded-full font-semibold hover:bg-white hover:text-slate-900 transition-all duration-300 transform hover:scale-105 shadow-lg"
                >
                  Get Started for Free
                </button>
             </div>
             <div className="absolute top-[-50px] right-[-50px] w-80 h-80 bg-slate-800 rounded-full blur-3xl opacity-40"></div>
             <div className="absolute bottom-[-50px] left-[-50px] w-80 h-80 bg-slate-800 rounded-full blur-3xl opacity-40"></div>
          </div>
        </div>
      </section>

      {/* --- SECTION 4: PREMIUM CONVERSATIONAL E-COMMERCE WORKFLOWS --- */}
      <section className="bg-[#EEF2FC] py-16 md:py-24 lg:py-32 border-t border-slate-100 overflow-hidden">
        <div className="container mx-auto px-4 md:px-6 lg:px-12 text-center">
          <h2 className="text-3xl md:text-5xl lg:text-6xl font-medium text-slate-900 mb-6 tracking-tight mt-0 lg:-mt-24 leading-tight">
            Fast notifications, smooth operations — <br className="hidden md:block" />
            <span className="text-[#F46653]">SMS built for e-commerce success</span>
          </h2>

          <div className="relative mt-10 mb-10 lg:mt-20 lg:mb-20 overflow-hidden">
            {/* Gradients */}
            <div className="absolute inset-y-0 left-0 w-12 md:w-20 lg:w-40 z-10 bg-gradient-to-r from-[#EEF2FC] to-transparent"></div>
            <div className="absolute inset-y-0 right-0 w-12 md:w-20 lg:w-40 z-10 bg-gradient-to-l from-[#EEF2FC] to-transparent"></div>

            {/* Infinite Scroll Rows */}
            <div className="animate-scroll-left flex gap-4 lg:gap-8 mb-4 lg:mb-8">
              {[...row1, ...row1].map((tag, idx) => (
                <div
                  key={idx}
                  className="px-5 py-3 lg:px-10 lg:py-6 bg-white border border-slate-100 rounded-2xl lg:rounded-3xl text-slate-700 font-semibold text-sm md:text-base lg:text-xl whitespace-nowrap transition-all shadow-sm mx-2 lg:mx-4"
                >
                  {tag}
                </div>
              ))}
            </div>

            <div className="animate-scroll-right flex gap-4 lg:gap-8">
              {[...row2, ...row2].map((tag, idx) => (
                <div
                  key={idx}
                  className="px-5 py-3 lg:px-10 lg:py-6 bg-white border border-slate-100 rounded-2xl lg:rounded-3xl text-slate-700 font-semibold text-sm md:text-base lg:text-xl whitespace-nowrap transition-all shadow-sm mx-2 lg:mx-4"
                >
                  {tag}
                </div>
              ))}
            </div>
          </div>

          <p className="text-lg md:text-xl lg:text-2xl text-slate-600 font-normal max-w-4xl mx-auto leading-relaxed mb-10 md:mb-16 px-4">
            Empower your online store with intelligent SMS workflows that drive sales, recover carts, 
            and keep your customers coming back with every ping.
          </p>

          <button
            onClick={handleSignupClick}
            className="inline-flex items-center justify-center gap-3 w-full sm:w-auto px-8 md:px-10 py-4 md:py-5 text-white bg-slate-900 rounded-xl transition duration-300 shadow-xl hover:shadow-2xl text-lg md:text-xl font-semibold"
          >
            Start Your Growth Journey
            <Target className="w-5 h-5 md:w-6 md:h-6" />
          </button>
        </div>
      </section>

    </div>
  );
};

export default Ecommerce;